import {
  BulkCreateShiftmanagementShiftShiftsInput,
  EmployeesEmployeeSelectorInput,
  ShiftManagementAreasViewPageDocument,
  ShiftManagementBulkCreateShiftsDocument,
  ShiftManagementMainPageDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { buildAddShiftsInputParameters } from 'modules/shift_management/lib/buildAddShiftsInput'
import buildEmployeeIds from 'modules/shift_management/lib/buildEmployeeIds'
import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import type ShiftForm from 'modules/shift_management/lib/forms/ShiftForm'
import { shiftMutationFeedback } from 'modules/shift_management/lib/handleShiftMutationFeedback'
import { trackAddMutationEvent } from 'modules/shift_management/lib/trackEvents'
import { Leave, ShiftsRow } from 'modules/shift_management/lib/types'
import Shift from 'modules/shift_management/store/internal/models/Shift'

import { DaysByRow } from './reducers/selection'
import { useRefreshStats } from './refreshShiftsRows'
import { ShiftInputsAttributesByDate } from './useDefaultAttributesFromRows'
import { useSchedule } from './useSchedule'
import { useSelectionDispatch, useSelectionState } from './useSelection'
import { useShiftsRows } from './useShiftsRows'
import { useUI } from './useUI'

interface BuildParams {
  defaultShiftId?: string
}

export const useMutateCustomShifts = ({
  employeeSelection,
  defaultAttributes,
  forms,
  daysByRow,
  weekDays,
  from,
  plannedBreakForms,
  advancedOptions,
  isBackup,
  leave,
}: {
  employeeSelection: EmployeesEmployeeSelectorInput
  defaultAttributes: ShiftInputsAttributesByDate
  forms: ShiftForm[]
  plannedBreakForms?: PlannedBreakForm[]
  daysByRow: DaysByRow
  weekDays: string
  from?: 'modal' | 'side_panel' | 'quick_actions'
  advancedOptions: boolean
  isBackup: boolean
  leave?: Leave
}) => {
  const { isEnabled: hasSelection } = useSelectionState()
  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [, dispatchUI] = useUI()
  const dispatchSelection = useSelectionDispatch()

  const { refreshStats } = useRefreshStats()

  const [addShifts, { loading }] = useMutation(ShiftManagementBulkCreateShiftsDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkCreateShiftmanagementShift,
  })

  const buildShiftsInput = ({
    defaultShiftId,
  }: BuildParams): BulkCreateShiftmanagementShiftShiftsInput[] => {
    const shifts = forms.flatMap((form: ShiftForm) => {
      return buildAddShiftsInputParameters({
        view,
        hasSelection,
        defaultAttributesByDate: defaultAttributes,
        name: form.get('name').value,
        unparsedStartTime: form.get('start_time').value,
        unparsedEndTime: form.get('end_time').value,
        daysByRow: daysByRow,
        defaultShiftId,
        extraHours: form.get('extra_hours').value,
        notes: form.get('notes').value,
        locationId: form.get('location_id').value,
        workAreaId: form.get('work_area_id').value,
        color: null,
        templateWeekId: null,
        unassignedShiftId: null,
        state: isBackup ? 'backup' : 'draft',
        leaveId: leave?.id,
        companyId: currentCompanyId,
      })
    })

    return shifts
  }

  const onSubmit = async ({
    defaultShiftId,
    closePortal,
  }: BuildParams & { closePortal?: () => void }) => {
    const { startFeedback, onSuccess, onError } = shiftMutationFeedback({
      progressMessage: i18n.t('shift_management.actions.creating_shifts'),
      successMessage: i18n.t('shift_management.actions.add_shifts_success'),
      closePortal,
    })

    startFeedback()

    const shifts = buildShiftsInput({ defaultShiftId })
    const plannedBreaks = plannedBreakForms?.map((form) => {
      return {
        startAt: form.get('start_at').value,
        endAt: form.get('end_at').value,
        duration: +form.get('duration').value,
        breakType: form.get('break_type').value,
        breakConfigurationId: form.get('break_configuration.id').value,
      }
    })
    const result = await addShifts({
      variables: {
        shifts,
        plannedBreaks,
        // If has manual selection in frontend, we build the object using daysByRow and send it to the backend.
        // The backend will handle the employee selection only if we use the EmployeeSelectorV2.
        employeeSelection: hasSelection ? undefined : employeeSelection,
      },
      // TODO: Actually update the cache properly
      refetchQueries: [ShiftManagementMainPageDocument, ShiftManagementAreasViewPageDocument],
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      const shiftsData = handled.data?.shifts || []
      const shiftsCreated = shiftsData.map((shift) =>
        Shift.buildFromGraphQL({
          shift,
          employeeFullName: shift.employee?.fullName || '',
          employeeAvatar: shift.employee?.avatar?.url || '',
          employeeLocationId: shift.employee?.locationId ?? '0',
        })
      )

      trackAddMutationEvent({
        view,
        isDefaultShift: false,
        weekDays,
        shifts,
        from,
        advancedOptions,
        isBackup,
      })

      dispatchShiftsRows({ type: 'ADD_SHIFTS', shifts: shiftsCreated, view })
      dispatchUI({ type: 'REFRESHING_STATS' })
      dispatchSchedule({ type: 'ADD_SHIFTS', shifts: shiftsCreated })
      dispatchSelection({ type: 'CLEAR_SELECTION' })

      // TODO: The logic here is to refresh the stats and calculate conflicts.
      // I'm not sure if we can move it to a different place and reduce responsibility.
      //
      const { shiftsReferenceKeys } = buildEmployeeIds(view, shiftsRows, shiftsCreated)

      const previousShiftsRows = shiftsRows.filter((row: ShiftsRow) => {
        return shiftsReferenceKeys.includes(row.key)
      })

      await refreshStats(previousShiftsRows)
      await onSuccess()
    } else {
      onError()
    }

    return handled
  }

  return { onSubmit, loading }
}
